import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Cpu, Workflow, ShieldCheck } from 'lucide-react';
import { useTheme } from './ThemeContext.jsx';

/**
 * Loading Screen Component
 * Matches the website's design aesthetic with dark/light mode support,
 * 3D effects, and smooth animations
 */
export default function LoadingScreen({ isLoading = true, onComplete }) {
  const { darkMode } = useTheme();
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    { icon: Cpu, text: 'Initializing AI Systems...' },
    { icon: Workflow, text: 'Loading Agent Orchestration...' },
    { icon: ShieldCheck, text: 'Applying Security Protocols...' },
    { icon: Sparkles, text: 'Ready for Innovation...' }
  ];

  useEffect(() => {
    if (!isLoading) return;

    const interval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + Math.random() * 15 + 5;
        
        // Update current step based on progress
        const stepIndex = Math.floor((newProgress / 100) * steps.length);
        setCurrentStep(Math.min(stepIndex, steps.length - 1));
        
        if (newProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            onComplete?.();
          }, 500);
          return 100;
        }
        return newProgress;
      });
    }, 150);

    return () => clearInterval(interval);
  }, [isLoading, onComplete, steps.length]);

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.1 }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
          className={`fixed inset-0 z-[100] flex items-center justify-center ${
            darkMode ? 'bg-[#0a0c10]' : 'bg-white'
          }`}
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 overflow-hidden">
            <div className={`absolute inset-0 opacity-30 ${
              darkMode 
                ? 'bg-gradient-to-br from-indigo-900/20 via-purple-900/10 to-cyan-900/20' 
                : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-cyan-50'
            }`} />
            
            {/* Floating Particles */}
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className={`absolute w-1 h-1 rounded-full ${
                  darkMode ? 'bg-white/20' : 'bg-gray-400/30'
                }`}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [-20, 20, -20],
                  opacity: [0.3, 0.8, 0.3],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>

          {/* Main Loading Content */}
          <div className="relative z-10 text-center max-w-md mx-auto px-6">
            {/* Logo */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="mb-8"
            >
              <div className={`w-20 h-20 mx-auto rounded-2xl flex items-center justify-center font-bold text-2xl text-white shadow-2xl ${
                darkMode 
                  ? 'bg-gradient-to-br from-indigo-500 to-cyan-400' 
                  : 'bg-gradient-to-br from-indigo-600 to-cyan-500'
              }`}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                >
                  ST
                </motion.div>
              </div>
            </motion.div>

            {/* Loading Text */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8"
            >
              <h2 className={`text-2xl font-bold mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Sikandar Tareen
              </h2>
              <p className={`text-sm ${
                darkMode ? 'text-white/70' : 'text-gray-600'
              }`}>
                Agentic AI Engineer
              </p>
            </motion.div>

            {/* Progress Bar */}
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: '100%', opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mb-6"
            >
              <div className={`h-2 rounded-full overflow-hidden ${
                darkMode ? 'bg-white/10' : 'bg-gray-200'
              }`}>
                <motion.div
                  className={`h-full rounded-full ${
                    darkMode 
                      ? 'bg-gradient-to-r from-indigo-500 to-cyan-400' 
                      : 'bg-gradient-to-r from-indigo-600 to-cyan-500'
                  }`}
                  style={{ width: `${progress}%` }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                />
              </div>
              <div className={`text-xs mt-2 ${
                darkMode ? 'text-white/60' : 'text-gray-500'
              }`}>
                {Math.round(progress)}%
              </div>
            </motion.div>

            {/* Current Step */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="flex items-center justify-center gap-3"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                >
                  {React.createElement(steps[currentStep].icon, {
                    size: 20,
                    className: darkMode ? 'text-cyan-400' : 'text-indigo-600'
                  })}
                </motion.div>
                <span className={`text-sm ${
                  darkMode ? 'text-white/80' : 'text-gray-700'
                }`}>
                  {steps[currentStep].text}
                </span>
              </motion.div>
            </AnimatePresence>

            {/* Sparkle Effects */}
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className={`absolute w-1 h-1 ${
                    darkMode ? 'bg-cyan-400' : 'bg-indigo-500'
                  }`}
                  style={{
                    left: `${20 + Math.random() * 60}%`,
                    top: `${20 + Math.random() * 60}%`,
                  }}
                  animate={{
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: Math.random() * 2,
                  }}
                />
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Hook for managing loading state
export function useLoadingScreen(duration = 3000) {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  return { isLoading, setIsLoading };
}